/**
 * TYPING ANIMATION HOOK
 * ChatGPT-style typing effect for bot responses
 */

import { useState, useEffect, useRef } from 'react';

interface UseTypingAnimationReturn {
  displayedText: string;
  isTyping: boolean;
  reset: () => void;
}

/**
 * Custom hook for ChatGPT-style typing animation
 * @param fullText - Complete text to display
 * @param speed - Speed in milliseconds per character (default: 30ms)
 * @param enabled - Whether animation is enabled (default: true)
 * @returns {UseTypingAnimationReturn} - Displayed text, typing state, and reset function
 */
export function useTypingAnimation(
  fullText: string,
  speed: number = 30,
  enabled: boolean = true
): UseTypingAnimationReturn {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const currentIndexRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef(false); // Track if animation has completed
  const previousFullTextRef = useRef(''); // Track the previous fullText to detect changes

  useEffect(() => {
    // If animation is disabled, show full text immediately
    if (!enabled) {
      setDisplayedText(fullText);
      setIsTyping(false);
      hasCompletedRef.current = true;
      previousFullTextRef.current = fullText;
      return;
    }

    // If animation already completed and text hasn't changed, don't restart
    if (hasCompletedRef.current && displayedText === fullText) {
      previousFullTextRef.current = fullText;
      return;
    }

    // Reset state when fullText actually changes
    setDisplayedText('');
    currentIndexRef.current = 0;
    setIsTyping(true);
    hasCompletedRef.current = false;
    previousFullTextRef.current = fullText;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // If fullText is empty, don't animate
    if (!fullText) {
      setIsTyping(false);
      hasCompletedRef.current = true;
      return;
    }

    // Start typing animation
    intervalRef.current = setInterval(() => {
      if (currentIndexRef.current < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndexRef.current + 1));
        currentIndexRef.current++;
      } else {
        // Animation complete
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        hasCompletedRef.current = true;
        setIsTyping(false);
      }
    }, speed);

    // Cleanup on unmount or when fullText changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullText, speed, enabled]);

  const reset = () => {
    setDisplayedText('');
    currentIndexRef.current = 0;
    setIsTyping(false);
    hasCompletedRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return {
    displayedText,
    isTyping,
    reset
  };
}

